<script setup lang="ts">
import { APP_DOWNLOAD_LINKS } from '@/utils/apostatudo/app-download-deep-link';

// Parede de fim do PWA (ENG-843). Substitui o site inteiro para quem abriu
// pelo ícone de um PWA/TWA instalado, mandando a pessoa para o app oficial.

// É bloqueio TOTAL por decisão de produto: sem botão de fechar, sem backdrop
// clicável, sem `Esc`, sem cookie de "já vi". Quem chegou aqui só sai pelas
// lojas. Por isso ela também não é um modal — o `app.vue` a renderiza NO LUGAR
// do `NuxtLayout`, então não há site montado por trás.

// Quem decide se esta tela aparece é o `usePwaShutdown()`, não este componente.
const alwaysLocked = ref(true);

useBodyScrollLock(alwaysLocked);

const TITLE =
  'Este atalho foi desativado. Baixe o aplicativo oficial para continuar.';
</script>

<template>
  <div class="pwa-wall">
    <div class="pwa-wall__card">
      <AppDownloadCard
        :links="APP_DOWNLOAD_LINKS.pwaWall"
        :title="TITLE"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
// Mesma forma do modo manutenção (`base/maintenance/Maintenance.vue`): cobre
// tudo e fica acima de qualquer coisa. O z-index é o do manutenção menos um —
// se os dois estiverem ativos, manutenção ganha, que é a precedência certa.

// O fundo NÃO é uma cor chapada: ele repete os dois extremos do próprio cartaz —
// `#030614` no topo, que é a cor da folha, e preto no pé, que é onde o `.ad-fade`
// do cartaz termina. Assim as duas emendas caem sobre a mesma cor dos dois lados
// e somem. Um fundo chapado faria o cartaz ler como um retângulo colado.
.pwa-wall {
  @apply fixed inset-0 flex items-center justify-center w-full h-full select-none z-[999998];

  background: linear-gradient(180deg, #030614 0%, #02030c 55%, #000 100%);
}

// O card tem `container-type` próprio; este wrapper existe só para centralizar.
// NÃO colocar `container-type` aqui nem em nada que ocupe a viewport: o cartaz
// mede tudo em `cqw` contra a largura da FOLHA, e mudar a base do `cqw`
// reescalaria a tipografia inteira (foi o que quebrou no ENG-799).
.pwa-wall__card {
  display: flex;
  justify-content: center;
  width: 100%;
}

// Aqui o cartaz não é uma folha flutuante, é a própria tela: sangra de borda a
// borda, sem canto arredondado e sem o halo do `box-shadow`. Eram justamente
// esses dois que desenhavam a moldura do retângulo sobre o fundo.

// A largura tem TETO pela altura, e isso não é preciosismo: a folha tem
// `aspect-ratio: 393/498`, então largura vira altura. Sem o teto, um celular
// deitado (844×390) daria uma folha de 1069px de altura centrada numa viewport
// de 390 — as pílulas das lojas saem da tela, e aqui não há scroll (o body está
// travado) nem botão de fechar. O `max-height: 100%` da folha NÃO resolve: o
// pai tem altura automática, então a porcentagem fica indefinida e é ignorada.

// Em retrato o `100vw` continua ganhando e nada muda; só em paisagem a folha
// vira uma coluna centrada, que é o preço de manter os botões alcançáveis.
.pwa-wall__card :deep(.ad-sheet) {
  width: min(100vw, calc(100dvh * 393 / 498));
  max-width: none;
  border-radius: 0;
  box-shadow: none;
}

// CELULAR DEITADO. Em pé o cartaz é a tela inteira; deitado ele não cabe — a
// folha tem `aspect-ratio: 393/498`, então caber na altura a espremeria numa
// coluna estreita com dois terços da tela mortos ao lado.

// Aqui a composição deita: logo, frase e ficha da loja de um lado, os botões do
// outro. A arte da mão SAI: ela é vertical, ocupa metade da folha em pé e
// deitada só roubaria a altura que os botões precisam.

// O gatilho é a ALTURA, não a orientação sozinha: um tablet deitado tem altura
// sobrando e o cartaz em pé continua bonito nele.
@media (orientation: landscape) and (max-height: 560px) {
  .pwa-wall {
    // Sem a arte, o brilho do cartaz sumiria junto e a tela ficaria chapada.
    // Este é o mesmo azul do `.ad-glow`, espalhado na largura que agora existe.
    background:
      radial-gradient(
        ellipse 70% 120% at 50% 100%,
        rgba(29, 63, 224, 0.28),
        rgba(29, 63, 224, 0) 70%
      ),
      linear-gradient(180deg, #030614 0%, #02030c 55%, #000 100%);
  }

  .pwa-wall__card {
    // Todas as medidas daqui para baixo são em `dvh`, e não em `cqw`: deitado a
    // dimensão escassa é a ALTURA, e a folha passa a ocupar a largura toda —
    // manter `cqw` faria `1cqw` saltar de 3,93px para ~9px e estourar o texto.
    // Cada `min()` tem um teto em px para a composição não inchar num tablet.
    :deep(.ad-sheet) {
      display: grid;
      align-content: center;
      width: 100vw;
      height: 100dvh;
      max-height: none;

      // A direita fica reservada para os botoes, que saem do fluxo logo abaixo.
      padding: 0 calc(min(38dvh, 190px) + min(11dvh, 60px)) 0 min(6dvh, 32px);
      row-gap: min(2.5dvh, 14px);
      // A proporção fixa é justamente o que não serve deitado.
      aspect-ratio: auto;
      // O fundo é o da parede; uma cor chapada aqui cobriria o brilho.
      background: transparent;
    }

    // A arte vertical e o fade que existia só para escurecer o pé dela.
    :deep(.ad-hand),
    :deep(.ad-fade),
    :deep(.ad-glow) {
      display: none;
    }

    // O cartaz posiciona tudo em absoluto contra a folha; deitado o grid é que
    // manda, então os filhos voltam para o fluxo.
    :deep(.ad-head),
    :deep(.ad-title),
    :deep(.ad-stores),
    :deep(.ad-meta) {
      position: static;
      inset: auto;
    }

    :deep(.ad-head) {
      justify-content: flex-start;
      height: auto;
    }

    :deep(.ad-logo) {
      width: auto;
      height: min(5.5dvh, 28px);
    }

    :deep(.ad-title) {
      font-size: min(5.5dvh, 26px);
      line-height: 1.25;
      text-align: left;
    }

    :deep(.ad-meta) {
      justify-content: flex-start;
      gap: min(4dvh, 22px);
    }

    // Os botões ocupam a coluna da direita inteira, empilhados: deitado eles são
    // a única ação da tela e precisam do maior alvo de toque possível.
    // FORA do fluxo do grid, e nao numa segunda coluna: como os dois botoes
    // empilhados sao mais altos que logo + frase + ficha juntos, uma coluna que
    // cruzasse as tres linhas esticaria todas elas para caber, abrindo vaos
    // entre o logo e a frase. Absoluto, eles se centram sozinhos e nao mexem
    // na altura de nada.
    :deep(.ad-stores) {
      position: absolute;
      top: 50%;
      right: min(6dvh, 32px);
      display: flex;
      flex-direction: column;
      gap: min(2.5dvh, 14px);
      transform: translateY(-50%);
    }

    :deep(.ad-store) {
      gap: min(1.6dvh, 9px);
      width: min(38dvh, 190px);
      height: min(12dvh, 52px);
    }

    :deep(.ad-store__icon) {
      width: min(6.5dvh, 30px);
    }

    :deep(.ad-store__text) small {
      font-size: min(2.9dvh, 14px);
    }

    :deep(.ad-store__text) strong {
      font-size: min(3.8dvh, 18px);
    }

    :deep(.ad-meta__item) {
      gap: min(0.8dvh, 4px);
    }

    :deep(.ad-meta__value) {
      gap: min(0.7dvh, 4px);
      font-size: min(3.8dvh, 18px);
    }

    :deep(.ad-meta__star),
    :deep(.ad-meta__label) {
      font-size: min(3.2dvh, 15px);
    }

    :deep(.ad-meta__age) {
      width: min(5.5dvh, 26px);
      height: min(5.5dvh, 26px);
      border-radius: min(0.9dvh, 5px);
      font-size: min(3.1dvh, 15px);
    }

    :deep(.ad-meta__divider) {
      height: min(6dvh, 28px);
    }
  }
}
</style>
